<template>
  <v-row>
    <v-col class="xs mt-2" offset-lg="1" lg="10">
      <v-breadcrumbs :items="items" divider=">" class="pl-1" />

      <v-card class="mt-10">
        <v-card-title class="theme">
          <v-icon class="white--text">beach_access</v-icon>
          <span class="headline ml-2 white--text">My time off requests</span>
          <v-spacer />
          <v-btn
            v-if="companyInfo.status==='Active'"
            @click="openTimeoffForm"
            depressed
            class="rounded-sm action white--text"
            >Request time off
          </v-btn>
        </v-card-title>
        <v-divider />
        <v-card-text>
          <h1 class="display-1 text-xs-center">
            {{ totalTimeoffs }}
          </h1>
          <div class="subtitle-1 text-xs-center mb-1">
            vacation days available
          </div>
          <template v-if="aggregate">
            <v-chip
              v-for="(item, i) in aggregate"
              :key="i"
              class="mt-2 mr-2"
              small
              chip
            >
              {{ item.name }}: {{ item.days }}
            </v-chip>
          </template>
          <v-divider class="mt-5"></v-divider>
          <v-data-table
            flat
            v-if="timeOffs"
            :items="timeOffs"
            hide-default-footer
            class="row-pointer"
            disable-pagination
            :headers="headers"
            @click:row="showEntity"
          >
            <template v-slot:item.text="{ item }">
              <td class="text-right">
                <v-tooltip left v-if="item.text">
                  <template v-slot:activator="{ on, attrs }">
                    <v-icon color="primary" dark v-bind="attrs" v-on="on"
                      >speaker_notes</v-icon
                    >
                  </template>
                  <span>{{ item.text }}</span>
                </v-tooltip>
              </td>
            </template>

            <template v-slot:item.type="{ item }">
              <td v-if="item.type === 'Sick Leave'">
                <v-icon small class="mr-2 mb-1 theme-text"
                  >medical_services</v-icon
                >{{ item.type }}
              </td>
              <td v-else-if="item.type === 'Vacation'">
                <v-icon small class="mr-2 mb-1 theme-text">beach_access</v-icon>
                {{ item.type }}
              </td>
              <td v-else>
                <v-icon small class="mr-2 mb-1 theme-text">grass</v-icon>Others
              </td>
            </template>

            <template v-slot:item.start_date="{ item }">
              <td>{{ item.start_date_formatted }}</td>
            </template>

            <template v-slot:item.end_date="{ item }">
              <td>{{ item.end_date_formatted }}</td>
            </template>

            <template v-slot:item.status="{ item }">
              <td class="text-right">
                <div v-if="item.status === 'Approved'" class="success--text">
                  <v-icon class="success--text mr-1" small>done</v-icon>
                  {{ item.status }}
                </div>
                <div v-if="item.status === 'Pending'" class="action-text">
                  <v-icon class="action-text mr-1" small>alarm</v-icon>
                  {{ item.status }}
                </div>
                <div v-if="item.status === 'Rejected'" class="error--text">
                  <v-icon class="error--text mr-1" small>cancel</v-icon>
                  {{ item.status }}
                </div>
              </td>
            </template>
            <template v-slot:no-data>No data</template>
            <template v-slot:footer>
              <div class="text-center mt-5">
                <v-btn
                  fab
                  small
                  color="theme white--text"
                  depressed
                  @click="previous"
                  :disabled="pagination.page <= 0"
                >
                  <v-icon small>navigate_before</v-icon>
                </v-btn>
                <span>
                  <v-btn text :disabled="true">{{ pagination.page + 1 }}</v-btn>
                </span>
                <v-btn
                  fab
                  small
                  depressed
                  color="theme white--text"
                  @click="next"
                  :disabled="pagination.disableNext"
                >
                  <v-icon small>navigate_next</v-icon>
                </v-btn>
              </div>
            </template>
          </v-data-table>
        </v-card-text>
      </v-card>
    </v-col>
    <v-dialog v-model="add_entity_dialog" persistent max-width="500px">
      <v-card flat>
        <v-card-title class="theme">
          <span class="headline white--text">Request time off</span>
          <v-spacer />
          <v-btn icon @click="closeAddTimeoffForm">
            <v-icon class="white--text">close</v-icon>
          </v-btn>
        </v-card-title>
        <v-divider />
        <v-card-text class="mt-5 mb-n8">Request type:</v-card-text>
        <v-card-actions class="px-6 text-center">
          <v-btn-toggle tile group color="theme" class="text-center">
            <v-btn
              class="rounded-sm theme-text text-capitalize"
              @click="setLeaveType('Vacation')"
            >
              <v-icon small class="mr-1">beach_access</v-icon>
              Vacation
            </v-btn>
            <v-btn
              class="rounded-sm theme-text text-capitalize"
              @click="setLeaveType('Sick Leave')"
            >
              <v-icon small class="mr-1">medical_services</v-icon>
              Sick Leave
            </v-btn>
            <v-btn
              class="rounded-sm theme-text text-capitalize"
              @click="setLeaveType('Others')"
            >
              <v-icon small class="mr-1">grass</v-icon>
              others
            </v-btn>
          </v-btn-toggle>
        </v-card-actions>
        <v-card-text>
          <v-divider />
          <v-row>
            <v-col cols="12">
              <v-alert
                type="warning"
                text
                border="bottom"
                :icon="false"
                v-if="apply_message"
                class="text-capitalize"
                >{{ apply_message }}
              </v-alert>
            </v-col>
          </v-row>
          <v-form ref="addTimeoffForm" v-model="addTimeOffFormValidation">
            <v-row>
              <v-col cols="12">
                <v-select
                  :items="selectedLeaves"
                  :rules="[validationRules.required]"
                  v-model="leaveType"
                  :return-object="true"
                  item-text="name"
                  persistent-hint
                  label="Select time off*"
                  clearable
                  class="mb-8 mt-n4"
                />
                <v-divider />
              </v-col>

              <v-col cols="12" sm="6">
                <v-dialog
                  ref="dialog"
                  v-model="startDateModal"
                  :return-value.sync="startDate"
                  persistent
                  width="290px"
                >
                  <template v-slot:activator="{ on }">
                    <v-text-field
                      persistent-hint
                      label="Start date*"
                      :rules="[validationRules.required]"
                      v-model="computedStartDateFormatted"
                      readonly
                      placeholder="Example: 31-12-2020"
                      v-on="on"
                    />
                  </template>
                  <v-date-picker
                    v-model="startDate"
                    scrollable
                    class="mt-0 pt-0"
                    :first-day-of-week="1"
                  >
                    <v-btn
                      text
                      class="theme-text"
                      @click="startDateModal = false"
                      >Cancel
                    </v-btn>
                    <v-spacer></v-spacer>
                    <v-btn
                      text
                      class="theme-text"
                      @click="$refs.dialog.save(startDate)"
                      >OK
                    </v-btn>
                  </v-date-picker>
                </v-dialog>
              </v-col>
              <v-col cols="12" sm="6">
                <v-dialog
                  ref="dialog2"
                  v-model="endDateModal"
                  :return-value.sync="endDate"
                  persistent
                  width="290px"
                >
                  <template v-slot:activator="{ on }">
                    <v-text-field
                      persistent-hint
                      label="End date*"
                      :rules="[validationRules.required]"
                      v-model="computedEndDateFormatted"
                      readonly
                      placeholder="Example: 31-12-2020"
                      v-on="on"
                    />
                  </template>
                  <v-date-picker
                    v-model="endDate"
                    scrollable
                    class="mt-0 pt-0"
                    :first-day-of-week="1"
                  >
                    <v-btn
                      text
                      class="theme-text"
                      @click="endDateModal = false"
                      >Cancel
                    </v-btn>
                    <v-spacer />
                    <v-btn
                      text
                      class="theme-text"
                      @click="$refs.dialog2.save(endDate)"
                      >OK
                    </v-btn>
                  </v-date-picker>
                </v-dialog>
              </v-col>
              <v-col cols="12">
                <v-textarea
                  :rules="[validationRules.required]"
                  label="Add a comment*"
                  v-model="description"
                  rows="3"
                />
              </v-col>
              <v-col cols="12" sm="6" class="mt-n5">
                <v-select
                  :items="approvers"
                  :rules="[validationRules.required]"
                  v-model="approver"
                  persistent-hint
                  label="Approver for this time off*"
                  :return-object="true"
                  clearable
                />
              </v-col>
              <v-col cols="12" sm="6" class="mt-n5 text-right">
                <v-btn
                  depressed
                  class="save white--text rounded-sm ml-0 mt-5"
                  @click="saveTimeoff"
                  >Submit request
                </v-btn>
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-dialog v-model="view_entity_dialog" persistent max-width="500px">
      <v-card flat>
        <v-card-title class="theme">
          <span class="headline white--text"
            >TimeOff Request {{ currentItem.status }}</span
          >
          <v-spacer></v-spacer>
          <v-btn icon @click="view_entity_dialog = !view_entity_dialog">
            <v-icon class="white--text">close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text>
          <v-row v-if="currentItem.status === 'Pending'" class="mt-3">
            <v-spacer />
            <v-btn
              class="rounded-sm error mr-4"
              depressed
              @click="deleteEntity(currentItem)"
              >Delete
            </v-btn>
          </v-row>
          <v-row class="mt-4">
            <v-col cols="12" sm="5" class="hidden-xs-only text-right">
              <v-icon x-large class="theme-text">date_range</v-icon>
              <div class="theme-text title">
                {{ getDay(currentItem.start_date) }}
              </div>
              <div>{{ currentItem.start_date_formatted }}</div>
            </v-col>
            <v-col cols="12" sm="2" class="hidden-xs-only text-center">
              <v-icon x-large class="theme-text">arrow_right_alt</v-icon>
            </v-col>
            <v-col cols="12" sm="5" class="hidden-xs-only text-left">
              <v-icon x-large class="theme-text">date_range</v-icon>
              <div class="theme-text title">
                {{ getDay(currentItem.end_date) }}
              </div>
              <div>{{ currentItem.end_date_formatted }}</div>
            </v-col>

            <v-col cols="12" class="hidden-sm-and-up text-center">
              <div>
                <v-icon x-large class="theme-text">date_range</v-icon>
                <div class="theme-text title">
                  {{ getDay(currentItem.start_date) }}
                </div>
                <div>{{ currentItem.start_date_formatted }}</div>
              </div>
              <v-icon x-large class="theme-text">arrow_right_alt</v-icon>
              <div>
                <v-icon x-large class="theme-text">date_range</v-icon>
                <div class="theme-text title">
                  {{ getDay(currentItem.end_date) }}
                </div>
                <div>{{ currentItem.end_date_formatted }}</div>
              </div>
            </v-col>

            <v-col cols="12" class="text-center">
              <div
                v-if="currentItem.status === 'Approved'"
                class="title success--text"
              >
                <v-icon class="success--text">done</v-icon>
                {{ currentItem.status }}
              </div>
              <div
                v-else-if="currentItem.status === 'Rejected'"
                class="title error--text"
              >
                <v-icon class="error--text">close</v-icon>
                {{ currentItem.status }}
              </div>
              <div v-else class="title warning--text">
                <v-icon class="warning--text">alarm</v-icon>
                {{ currentItem.status }}
              </div>
            </v-col>
            <v-col cols="12">
              <div class="subtitle-1 theme-text">Description</div>
              <div class="mb-3">{{ currentItem.description }}</div>
              <v-divider />
              <div class="subtitle-1 mt-3 theme-text">Message</div>
              <div>{{ currentItem.text }}</div>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
    </v-dialog>
  </v-row>
</template>
<script>
import { mapGetters } from "vuex";

import { Actions } from "@/views/actions";
import { Breadcrumbs } from "@/views/breadcrumbs";
import { Component } from "@/views/component";
import { Constants } from "@/views/constants";
import { TableHeaders } from "@/views/headers";

export default {
  name: Component.Protected.TimeOff.List,
  computed: {
    ...mapGetters(["userInfo", "timeOffs", "companyInfo"]),
    computedStartDateFormatted() {
      if (this.startDate) {
        return this.datePickerFormat(this.startDate);
      }
      return null;
    },
    computedEndDateFormatted() {
      if (this.endDate) {
        return this.datePickerFormat(this.endDate);
      } else {
        return null;
      }
    },
    totalTimeoffs() {
      let count = 0;
      if (this.aggregate) {
        this.aggregate.forEach((el) => {
          if (el.type === "Vacation") {
            count += el.days;
          }
        });
      }
      return count;
    },
  },
  data: () => ({
    leaveTypes: [],
    endDateModal: false,
    startDateModal: false,
    items: Breadcrumbs.TimeOff.List,
    headers: TableHeaders.TimeOff.List,
    addTimeOffFormValidation: true,
    add_entity_dialog: false,
    view_entity_dialog: false,
    currentItem: {},
    approvers: [],
    approver: {},
    description: null,
    leaveType: null,
    startDate: null,
    endDate: null,
    days: [
      "Sunday",
      "Monday",
      "Tuesday",
      "Wednesday",
      "Thursday",
      "Friday",
      "Saturday",
    ],
    apply_message: "",
    selectedLeaves: [],
    search: { limit: 10, offset: 0 },
    pagination: { disableNext: true, page: 0 },
    aggregate: [],
  }),
  methods: {
    next() {
      if (this.pagination.page < 0) {
        this.pagination.page = 0;
        this.search.offset = 0;
      } else if (this.timeOffs.length <= this.search.limit) {
        this.pagination.page++;
        this.search.offset = this.search.limit * this.pagination.page;
        this.getTimeOffs();
      }
    },
    previous() {
      this.pagination.page--;
      if (this.pagination.page < 0) {
        this.pagination.page = 0;
        this.search.offset = 0;
      } else {
        this.search.offset = this.search.limit * this.pagination.page;
        this.getTimeOffs();
      }
    },
    setLeaveType(tt) {
      console.log(tt);
      this.selectedLeaves = this.leaveTypes.filter((el) => {
        console.log({ ...el });
        return el.type === tt;
      });
      this.$forceUpdate();
      console.log(this.selectedLeaves);
    },
    showEntity(e) {
      if (parseInt(e.user_id) === parseInt(this.userInfo.userID)) {
        this.currentItem = { ...e };
        this.view_entity_dialog = true;
      } else {
        return false;
      }
    },
    ddmmyyyy(item) {
      if (!item) return false;
      let dt = item.split("-");
      if (dt.length !== 3) return false;
      let year = dt[0];
      let mm = `${dt[1]}`;
      let dd = dt[2];
      return `${dd.padStart(2, "0")}-${mm.padStart(2, "0")}-${year}`;
    },
    // from yyyy-mm-dd to a day
    getDay(item) {
      if (!item) return false;
      let dt = item.split("-");
      if (dt.length !== 3) return false;

      let year = parseInt(dt[0]);
      let mm = parseInt(dt[1] - 1);
      let dd = parseInt(dt[2]);
      let date = new Date(year, mm, dd);

      return this.days[date.getDay()];
    },
    resetModel() {
      this.endDate = new Date().toISOString().substring(0, 10);
      this.startDate = new Date().toISOString().substring(0, 10);
      this.leaveType = null;
      this.description = null;
    },
    getTimeOffs() {
      this.$store
        .dispatch(Actions.TimeOff.Find, {
          ...this.search,
          type: Constants.TimeOff.Criteria.Me,
        })
        .then(() => {
          let len = this.timeOffs.length;
          if ((len < this.search.limit && len > 0) || len === 0) {
            this.pagination.disableNext = true;
          } else if (len === this.search.limit) {
            this.pagination.disableNext = false;
          }
        })
        .catch((err) => {
          console.error(Constants.TimeOff.Messages.Error.Find, err);
          this.snackbar = true;
          this.snackbarMessage = Constants.TimeOff.Messages.Error.Find;
        });
    },
    openTimeoffForm() {
      if (this.$refs.addTimeoffForm) {
        this.$refs.addTimeoffForm.resetValidation();
        this.startDate = new Date().toISOString().substring(0, 10);
      }
      this.apply_message = "";
      this.add_entity_dialog = true;
    },
    closeAddTimeoffForm() {
      this.resetModel();
      this.add_entity_dialog = false;
    },
    saveTimeoff() {
      this.apply_message = "";
      if (this.$refs.addTimeoffForm.validate()) {
        let data = {
          approver_id: this.approver.id,
          approver_name: this.approver.text,
          leave_type: this.leaveType.name,
          leave_id: this.leaveType.id,
          description: this.description,
          status: Constants.TimeOff.Status.Pending,
          end_date: this.computedEndDateFormatted,
          start_date: this.computedStartDateFormatted,
        };
        this.$store
          .dispatch(Actions.TimeOff.Add, data)
          .then((res) => {
            if (res.status === 200) {
              if (res.data.error === "true") {
                this.apply_message = res.data.message;
              } else {
                this.add_entity_dialog = false;
                this.resetModel();
                this.$refs.addTimeoffForm.reset();
              }
            }
          })
          .catch((err) => {
            console.error(Constants.TimeOff.Messages.Error.Add, err);
            this.snackbar = true;
            this.snackbarMessage = Constants.TimeOff.Messages.Error.Add;
          })
          .finally(() => {
            this.getTimeOffs();
          });
      }
    },
    approveEntity(el) {
      if (this.userInfo.role === Constants.Roles.HR) {
        this.$router.push({
          name: Component.Protected.TimeOff.Edit,
          params: { id: el.id },
        });
      }
    },
    deleteEntity(el) {
      if (
        this.userInfo.role === Constants.Roles.HR ||
        (this.userInfo.role === Constants.Roles.Employee &&
          el.user_id == this.userInfo.userID)
      ) {
        // ask if the user is sure
        let conf = confirm(
          "Are you sure? Confirming this will delete the the timeoff request"
        );
        if (conf) {
          this.$store
            .dispatch(Actions.TimeOff.Delete, el.id)
            .catch((err) => {
              console.error(Constants.TimeOff.Messages.Error.Delete, err);
              this.snackbarMessage = Constants.TimeOff.Messages.Error.Delete;
              this.snackbar = true;
            })
            .finally(() => {
              this.view_entity_dialog = false;
            });
        }
      }
    },
    setup() {
      this.$store.dispatch(Actions.TimeOff.Info).then((r) => {
        if (r.status === 200) {
          this.leaveTypes = [];
          this.approvers = [];
          r.data.leave_types.forEach((el) =>
            this.leaveTypes.push({ name: el.name, id: el.id, type: el.type })
          );
          r.data.approvers.forEach((el) =>
            this.approvers.push({ id: el.approver_id, text: el.approver_name })
          );
          this.selectedLeaves = this.leaveTypes;
        }
      });
      this.getTimeOffs();
      this.$store.dispatch(Actions.Company.Info);

      this.$store
        .dispatch(Actions.TimeOff.Aggregate)
        .then((r) => {
          if (r.status === 200) {
            this.aggregate = r.data;
          }
        })
        .catch((err) => {
          console.error(err);
        });
    },
  },
  created() {
    this.resetModel();
    if (this.userInfo) {
      this.setup();
    } else {
      this.$store.dispatch(Actions.Firebase.UserInfo).then(() => this.setup());
    }
  },
};
</script>
<style scoped>
* >>> div.v-picker__title {
  background-color: #551738 !important;
  border-color: #551738 !important;
}

/* * >>> div.v-date-picker-table {
  height: 250px !important;
} */
</style>